import fetchJSON from '@api/api_helper';
import { filter, dropRight, last, map } from "lodash";
import {
  CreateLabelOptions,
  GetLabelsResponseData,
  LabelTree,
  LabelTreeChild,
  isLabelTreeChild,
  GetLabelsResponse
} from './types/labelsTypes';

const { type: fxType, key: fxKey } = BF.fx.resource();

const url = (
  resourceType?: string,
  resourceKey?: string
): string => `/api/v4/${resourceType ?? fxType}s/${resourceKey ?? fxKey}/labels?per=3000`;

/**
 * GET labels. Defaults getting the first 3,000 labels (the API max). Pass in fetchAll as true to get all labels.
 * @param {boolean | undefined} fetchAll boolean (optional)
 * @returns {Promise<GetLabelsResponseData[]>} GetLabelsResponseData[] (array of labels)
 */
export const get = async (
  fetchAll?: boolean,
  resourceType?: string,
  resourceKey?: string,
  updateFetchControllers?: (controller: AbortController) => void,
): Promise<GetLabelsResponseData[]> => {
  let labels: GetLabelsResponseData[] = [];

  try {
    const firstPage = await fetchJSON(url(resourceType, resourceKey), {}, updateFetchControllers) as GetLabelsResponse;
    labels = firstPage.data;

    if (fetchAll && firstPage.meta.total_pages > 1) {
      // create a numeric array out of the remaining total_pages
      // for example, if total_pages = 4, remainingPages would be [2, 3, 4]
      const remainingPages = Array.from({ length: firstPage.meta.total_pages - 1 }, (_, k) => k + 2);

      // we use Promise.all to get the remaining labels from the API in parallel
      await Promise.all(
        remainingPages.map(async (page) => {
          const result = await fetchJSON(`${url(resourceType, resourceKey)}&page=${page}`, {}, updateFetchControllers) as GetLabelsResponse;
          labels = [...labels, ...result.data];
        })
      );
    }

    return Promise.resolve(labels);
  } catch (err) {
    throw (err?.name === 'AbortError' ? err : await err.json());
  }
};

export const create = (options: CreateLabelOptions): Promise<any> => (
  // example options: {name: 'Sales Team', position: 6, parent_key: 'abcd'}
  // omitted parent_key results in top level label
  // omitted position results in position 0
  fetchJSON(url(), {
    method: 'POST',
    body: {
      data: {
        attributes: {
          ...options
        }
      }
    }
  }).catch(async (response) => { throw (await response.json()); })
);

const buildTree = (parentLabel = {} as LabelTree | LabelTreeChild, allLabels = []): LabelTree => {
  // leverage typeguard. Unfortunately have to call it each time to set type of assignment
  const parentKey = isLabelTreeChild(parentLabel) ? parentLabel.id : undefined;
  const labelName = isLabelTreeChild(parentLabel) ? parentLabel.attributes.name : undefined;
  const children = filter(allLabels, { parentId: parentKey }).sort(
    (a, b) => a.attributes.position - b.attributes.position
  );
  const label = {
    ...parentLabel,
    title: labelName,
    name: labelName,
    key: parentKey,
    children: map(children, ((child) => buildTree(child, allLabels)))
  };

  return label;
};

// helper: returns tree object of all labels with their attributes
export const formatLabelsTree = (labels: GetLabelsResponseData[], expandedLabels = [] as string[]): LabelTree => {
  const labelsWithParentId = map(labels, ((label) => ({
    ...label,
    expanded: expandedLabels.includes(label.id),
    parentId: last(dropRight(label.attributes.path))
  })));
  const labelsTree = buildTree({} as LabelTree, labelsWithParentId);

  return labelsTree;
};

// helper: returns flat object of all labels with their attributes
// TODO set return type
// eslint-disable-next-line
export const formatLabelsFlat = (labels) => {
  const labelsFlat = {};

  labels.forEach((label) => { labelsFlat[label.id] = label.attributes; });

  return labelsFlat;
};
